import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import AlertsContext from "common/providers/alerts";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserVerification } from "types/UserVerification";
import UserSearch from "components/search/UserSearch";
import ButtonGroup from "common/components/ButtonGroup/ButtonGroup";
import UserVerificationItem from "./UserVerificationItem";

function UserVerifications() {
  const { t } = useTranslation();

  const { setAlert } = useContext(AlertsContext);

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<UserVerification>("user-verifications", {
    defaultSorts: ["updatedAt:DESC", "createdAt:DESC"],
    populate: [
      "user",
      "profile",
      "profile.avatar",
      "profile.companyLogo",
      "profile.services",
      "idRecto",
      "idVerso",
      "personalPicture",
      "companyRegister",
    ],
    defaultFilters: {
      status: "pending",
    },
  });

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, "danger");
    }
  }, [errorList1, setAlert]);

  const { removeItem } = useItem<UserVerification>("user-verifications", "");

  const { navigate } = useRouter();

  const handleNavigate = (id: any) => {
    navigate(`/user-verifications/${id}`);
  };

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const columns = useMemo(
    () => [
      {
        Header: t("forms.profile"),
        filterName: "user][username]$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t("forms.state"),
        filterName: "status",
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t("status.all") },
              { value: "pending", label: t("status.pending") },
              { value: "validated", label: t("status.validated") },
              { value: "denied", label: t("status.denied") },
            ]}
            {...props}
          />
        ),
      },
    ],
    [t]
  );

  const setOverallFilter = useCallback(
    (key: string, value: string | number | boolean) => {
      setFilter(key, value);
    },
    [setFilter]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.userVerifications")}
        Icon={CheckBadgeIcon}
        subTitle={t("titleLegends.userVerifications")}
        noAdd
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setOverallFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={UserVerificationItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default UserVerifications;
